import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getApiBaseUrl } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import type { SchemaFieldProps } from '../types';
import { translateError } from '../../utils/errorMessages';

/**
 * Resolve an image path to a displayable URL. Server uploads return a relative
 * path (`/uploads/...`); the admin app can run on a different origin than the
 * API, so a bare relative path would 404. Prepend the API base for relative
 * paths. Mirrors CoursePresentationForm / EventForm.
 */
const getImageUrl = (url: string): string =>
  url.startsWith('/') ? `${getApiBaseUrl()}${url}` : url;

/**
 * Custom SchemaForm widget for the activity `coverImage` field. Matches the
 * course presentation cover UX: an upload button, a live preview, and a remove
 * button — no raw URL text input. The stored value is the returned image path.
 *
 * Previously this used `<input type="url">`, which (a) rejected uploaded
 * relative paths as "not a URL" via native validation — blocking save — and
 * (b) didn't resolve relative paths for the preview. Uploads go through the
 * generic gallery endpoint (no activity id required), so it works in both the
 * create and edit flows.
 */
export interface CoverImageFieldProps extends SchemaFieldProps {
  /** Retained for API compatibility; uploads no longer require an activity id. */
  activityId?: number;
}

const CoverImageField: React.FC<CoverImageFieldProps> = ({ field, value, onChange, error }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const coverImage: string = value ?? '';

  const validateImageFile = (file: File): boolean => {
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      addToast(t('events.galleryInvalidType'), 'error');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      addToast(t('events.galleryFileTooLarge'), 'error');
      return false;
    }
    return true;
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateImageFile(file)) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const { data } = await api.uploads.galleryImage(file);
      onChange(data.url);
      addToast(t('activities.coverUploadSuccess'), 'success');
    } catch (err: any) {
      addToast(translateError(err, t, 'activities.coverUploadError'), 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeCover = async () => {
    // Best-effort server cleanup for uploaded (relative) paths; external URLs
    // are left as-is. Never block the field clear on a failed delete.
    if (coverImage.startsWith('/')) {
      try {
        await api.uploads.deleteGalleryImage(coverImage);
      } catch {
        /* non-fatal */
      }
    }
    onChange('');
  };

  return (
    <FormField
      htmlFor="sf-coverImage"
      label={t(field.labelKey, field.name)}
      labelHelpText={t('activities.coverImageHelp')}
      required={field.required}
      error={error}
    >
      <input
        ref={fileInputRef}
        id="sf-coverImage"
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={handleUpload}
        aria-label={t('activities.uploadCover')}
      />
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {t('activities.uploadCover')}
        </Button>
      </div>

      {coverImage && (
        <div style={{ marginTop: '0.75rem' }}>
          <img
            src={getImageUrl(coverImage)}
            alt=""
            style={{
              maxWidth: '240px',
              maxHeight: '160px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              objectFit: 'cover',
              display: 'block',
            }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={removeCover}
            style={{ marginTop: '0.5rem' }}
          >
            {t('activities.removeCover')}
          </Button>
        </div>
      )}
    </FormField>
  );
};

export default CoverImageField;
